
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, BarChart2, Calendar, CheckCircle, Clock, LayoutDashboard, Mail, MapPin, MessageSquare, Phone, RefreshCw, TrendingUp, User } from "lucide-react";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PartnerDashboardMetrics from "@/components/partners/PartnerDashboardMetrics";
import PartnerSMSMetrics from "@/components/partners/PartnerSMSMetrics";
import PartnerEmailMetrics from "@/components/partners/PartnerEmailMetrics";
import PartnerLeadsList from "@/components/partners/PartnerLeadsList";
import { ServiceType, LeadStageType } from "@/context/LeadContext";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

interface Partner {
  id: string;
  name: string;
  phone: string;
  email: string;
  location: string;
  postalCode: string;
  services: string[];
  createdAt: string;
}

interface Lead {
  id: string;
  name: string;
  phone: string;
  address: string;
  postalCode: string;
  service: ServiceType;
  timeframe: string;
  comments?: string;
  stage: LeadStageType;
  partnerId: string | null;
  partnerName: string | null;
  createdAt: string;
  updatedAt: string;
  smsStatus: "sent" | "delivered" | "failed" | null;
  viewed: boolean;
  viewedAt: string | null;
  latitude: number | null;
  longitude: number | null;
}

const PartnerLeadsDashboard = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [leadStats, setLeadStats] = useState<any>([]);
  const [timeFilter, setTimeFilter] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const { toast } = useToast();

  const calculateLeadScore = useCallback((lead: Lead) => {
    let score = 50;

    if (lead.timeframe === "άμεσα") score += 25;
    if (lead.timeframe === "εντός3μηνών") score += 12;
    if (lead.comments && lead.comments.trim().length > 15) score += 8;
    if (lead.viewed) score += 10;
    if (lead.stage === "Νέο") score += 10;
    if (lead.stage === "σεΕκκρεμότητα") score -= 5;

    return Math.max(0, Math.min(100, score));
  }, []);

  const fetchPartnerAndLeads = useCallback(async (showLoader = true) => {
    if (!partnerId) return;

    try {
      if (showLoader) {
        setIsLoading(true);
      }

      const { data: partnerData, error: partnerError } = await supabase
        .from('partners')
        .select('*')
        .eq('id', partnerId)
        .single();

      if (partnerError) {
        throw new Error(`Error fetching partner: ${partnerError.message}`);
      }

      const formattedPartner: Partner = {
        id: partnerData.id,
        name: partnerData.name,
        phone: partnerData.phone,
        email: partnerData.email || '',
        location: partnerData.location,
        postalCode: partnerData.radius?.toString() || '',
        services: partnerData.services || [],
        createdAt: partnerData.created_at,
      };

      setPartner(formattedPartner);

      let leadsQuery = supabase
        .from('leads')
        .select('*')
        .eq('partner_id', partnerId);

      const now = new Date();
      if (timeFilter === "week") {
        const lastWeek = new Date(now);
        lastWeek.setDate(lastWeek.getDate() - 7);
        leadsQuery = leadsQuery.gte('created_at', lastWeek.toISOString());
      } else if (timeFilter === "month") {
        const lastMonth = new Date(now);
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        leadsQuery = leadsQuery.gte('created_at', lastMonth.toISOString());
      } else if (timeFilter === "quarter") {
        const lastQuarter = new Date(now);
        lastQuarter.setMonth(lastQuarter.getMonth() - 3);
        leadsQuery = leadsQuery.gte('created_at', lastQuarter.toISOString());
      } else if (timeFilter === "year") {
        const lastYear = new Date(now);
        lastYear.setFullYear(lastYear.getFullYear() - 1);
        leadsQuery = leadsQuery.gte('created_at', lastYear.toISOString());
      }

      const { data: leadsData, error: leadsError } = await leadsQuery
        .order('created_at', { ascending: false });

      if (leadsError) {
        throw new Error(`Error fetching leads: ${leadsError.message}`);
      }

      const formattedLeads: Lead[] = leadsData.map((lead: any) => ({
        id: lead.id,
        name: lead.name,
        phone: lead.phone,
        address: lead.address,
        postalCode: lead.postal_code || "",
        service: lead.service as ServiceType,
        timeframe: lead.timeframe,
        comments: lead.comments || undefined,
        stage: (lead.stage || 'Νέο') as LeadStageType,
        partnerId: lead.partner_id || null,
        partnerName: null,
        createdAt: lead.created_at,
        updatedAt: lead.updated_at || lead.created_at,
        smsStatus: lead.sms_status || null,
        viewed: lead.link_opened || false,
        viewedAt: lead.viewed_at,
        latitude: lead.latitude || null,
        longitude: lead.longitude || null,
      }));

      setLeads(formattedLeads);
      setLastUpdated(new Date());

      const stats = [
        {
          name: 'Συνολικά Leads',
          count: formattedLeads.length,
          color: 'bg-blue-500',
          bg: 'bg-blue-50',
          text: 'text-blue-700',
          icon: <User className="h-5 w-5" />
        },
        {
          name: 'Leads που Έκλεισαν',
          count: formattedLeads.filter(lead => lead.stage === 'έκλεισεΗΔουλειά').length,
          color: 'bg-emerald-500',
          bg: 'bg-emerald-50',
          text: 'text-emerald-700',
          icon: <CheckCircle className="h-5 w-5" />
        },
        {
          name: 'Leads σε Εκκρεμότητα',
          count: formattedLeads.filter(lead => lead.stage === 'σεΕκκρεμότητα').length,
          color: 'bg-amber-500',
          bg: 'bg-amber-50',
          text: 'text-amber-700',
          icon: <Clock className="h-5 w-5" />
        },
        {
          name: 'Νέα Leads',
          count: formattedLeads.filter(lead => lead.stage === 'Νέο').length,
          color: 'bg-purple-500',
          bg: 'bg-purple-50',
          text: 'text-purple-700',
          icon: <BarChart2 className="h-5 w-5" />
        },
      ];

      setLeadStats(stats);
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Σφάλμα",
        description: "Υπήρξε πρόβλημα κατά την ανάκτηση των δεδομένων.",
        variant: "destructive",
      });
    } finally {
      if (showLoader) {
        setIsLoading(false);
      }
    }
  }, [partnerId, timeFilter, toast]);

  useEffect(() => {
    fetchPartnerAndLeads();
  }, [fetchPartnerAndLeads]);

  useEffect(() => {
    if (!partnerId || !autoRefresh) return;

    const interval = window.setInterval(() => {
      fetchPartnerAndLeads(false);
    }, 30000);

    return () => window.clearInterval(interval);
  }, [partnerId, autoRefresh, fetchPartnerAndLeads]);

  const getConversionRate = () => {
    if (!leads.length) return 0;
    const closedLeads = leads.filter(lead => lead.stage === 'έκλεισεΗΔουλειά').length;
    return Math.round((closedLeads / leads.length) * 100);
  };

  const getServiceLabel = (service: string) => {
    switch (service) {
      case "εξοικονομώ": return "Εξοικονομώ";
      case "αντλίεςΘερμότητας": return "Αντλίες Θερμότητας";
      case "φωτοβολταϊκά": return "Φωτοβολταϊκά";
      case "κλιματιστικά": return "Κλιματιστικά";
      case "φυσικόΑέριο": return "Φυσικό Αέριο";
      case "ηλιακά": return "Ηλιακά";
      case "άλλο": return "Άλλο";
      default: return service;
    }
  };

  const getStageLabel = (stage: LeadStageType) => {
    switch (stage) {
      case "Νέο": return "Νέο";
      case "σεΕκκρεμότητα": return "Σε Εκκρεμότητα";
      case "ήθελεΠληροφορίες": return "Πληροφορίες";
      case "έκλεισεΗΔουλειά": return "Έκλεισε";
      default: return stage;
    }
  };

  const getStageBadgeClass = (stage: LeadStageType) => {
    switch (stage) {
      case "Νέο":
        return "bg-purple-50 text-purple-700 border-purple-200";
      case "σεΕκκρεμότητα":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "ήθελεΠληροφορίες":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "έκλεισεΗΔουλειά":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      default:
        return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  const pipelineCounts = useMemo(() => ({
    new: leads.filter((lead) => lead.stage === "Νέο").length,
    pending: leads.filter((lead) => lead.stage === "σεΕκκρεμότητα").length,
    info: leads.filter((lead) => lead.stage === "ήθελεΠληροφορίες").length,
    closed: leads.filter((lead) => lead.stage === "έκλεισεΗΔουλειά").length,
  }), [leads]);

  const topPriorityLeads = useMemo(() => {
    return [...leads]
      .sort((a, b) => calculateLeadScore(b) - calculateLeadScore(a))
      .slice(0, 5);
  }, [leads, calculateLeadScore]);

  const averageLeadScore = useMemo(() => {
    if (!leads.length) return 0;
    const totalScore = leads.reduce((sum, lead) => sum + calculateLeadScore(lead), 0);
    return Math.round(totalScore / leads.length);
  }, [leads, calculateLeadScore]);

  const urgentLeads = useMemo(() => {
    return leads.filter((lead) => lead.timeframe === "άμεσα").length;
  }, [leads]);

  const calculateMetrics = () => {
    if (!leads.length) return null;

    const totalLeads = leads.length;
    const closedDeals = leads.filter(lead => lead.stage === 'έκλεισεΗΔουλειά').length;
    const successRate = Math.round((closedDeals / totalLeads) * 100);

    const sentSMS = leads.filter(lead => lead.smsStatus === 'sent' || lead.smsStatus === 'delivered').length;
    const viewedSMS = leads.filter(lead => lead.viewed).length;
    const viewRate = sentSMS > 0 ? Math.round((viewedSMS / sentSMS) * 100) : 0;

    const now = new Date();
    const lastWeek = new Date(now);
    lastWeek.setDate(lastWeek.getDate() - 7);

    const lastMonth = new Date(now);
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    const leadsLastWeek = leads.filter(lead => new Date(lead.createdAt) >= lastWeek).length;
    const leadsLastMonth = leads.filter(lead => new Date(lead.createdAt) >= lastMonth).length;

    return {
      totalLeads,
      closedDeals,
      successRate,
      viewedSMS,
      sentSMS,
      viewRate,
      leadsLastWeek,
      leadsLastMonth
    };
  };

  const metrics = calculateMetrics();

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="flex flex-col items-center justify-center p-10 bg-slate-50 rounded-lg border border-dashed border-slate-200">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Ο συνεργάτης δεν βρέθηκε</h2>
        <p className="text-slate-600 mb-6">Δεν βρέθηκαν πληροφορίες για τον συγκεκριμένο συνεργάτη.</p>
        <Link to="/admin/partners">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Επιστροφή στους Συνεργάτες
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div className="flex items-start gap-4">
          <Link to="/admin/partners">
            <Button variant="outline" size="icon" className="rounded-full h-10 w-10 border-slate-200 mt-1">
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{partner.name}</h1>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                Ενεργός
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-2 text-sm text-slate-500">
              <div className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" />
                <span>{partner.phone}</span>
              </div>
              {partner.email && (
                <div className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  <span>{partner.email}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" />
                <span>{partner.location}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                <span>Από {format(new Date(partner.createdAt), 'dd/MM/yyyy')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant={autoRefresh ? "default" : "outline"}
            onClick={() => setAutoRefresh((prev) => !prev)}
          >
            Live {autoRefresh ? "ON" : "OFF"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => fetchPartnerAndLeads(false)}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Ανανέωση
          </Button>
          <Select value={timeFilter} onValueChange={setTimeFilter}>
            <SelectTrigger className="w-[180px] bg-white border-slate-200 print:hidden">
              <SelectValue placeholder="Χρονική περίοδος" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Όλες οι περίοδοι</SelectItem>
              <SelectItem value="week">Τελευταία εβδομάδα</SelectItem>
              <SelectItem value="month">Τελευταίος μήνας</SelectItem>
              <SelectItem value="quarter">Τελευταίο τρίμηνο</SelectItem>
              <SelectItem value="year">Τελευταίο έτος</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className={`inline-block h-2 w-2 rounded-full ${autoRefresh ? 'bg-emerald-500' : 'bg-slate-300'}`} />
        <span>{autoRefresh ? 'Live ενημέρωση ενεργή (30s)' : 'Live ενημέρωση ανενεργή'}</span>
        {lastUpdated && (
          <span>• Τελευταία ενημέρωση: {format(lastUpdated, 'dd/MM/yyyy HH:mm:ss', { locale: el })}</span>
        )}
      </div>

      {/* Services Tags */}
      <div className="flex flex-wrap gap-2">
        {partner.services && partner.services.length > 0 ? (
          partner.services.map((service, index) => (
            <Badge key={index} variant="secondary" className="bg-slate-100 text-slate-700 hover:bg-slate-200 font-normal">
              {getServiceLabel(service)}
            </Badge>
          ))
        ) : (
          <span className="text-sm text-slate-500 italic">Δεν έχουν οριστεί υπηρεσίες</span>
        )}
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
        <TabsList className="w-full justify-start border-b border-slate-200 bg-transparent p-0 h-auto rounded-none">
          <TabsTrigger
            value="overview"
            className="rounded-t-lg rounded-b-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2"
          >
            <LayoutDashboard className="h-4 w-4 mr-2" />
            Επισκόπηση
          </TabsTrigger>
          <TabsTrigger
            value="sms"
            className="rounded-t-lg rounded-b-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            Μηνύματα SMS
          </TabsTrigger>
          <TabsTrigger
            value="email"
            className="rounded-t-lg rounded-b-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2"
          >
            <Mail className="h-4 w-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger
            value="leads"
            className="rounded-t-lg rounded-b-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2"
          >
            <User className="h-4 w-4 mr-2" />
            Πελάτες / Leads
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 animate-fade-in">
          {metrics && (
            <PartnerDashboardMetrics
              totalLeads={metrics.totalLeads}
              closedDeals={metrics.closedDeals}
              successRate={metrics.successRate}
              viewedSMS={metrics.viewedSMS}
              sentSMS={metrics.sentSMS}
              viewRate={metrics.viewRate}
              leadsLastWeek={metrics.leadsLastWeek}
              leadsLastMonth={metrics.leadsLastMonth}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500">Μέσο Lead Score</p>
                <p className="text-2xl font-bold text-emerald-700 mt-1">{averageLeadScore}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500">Άμεσα Leads</p>
                <p className="text-2xl font-bold text-rose-700 mt-1">{urgentLeads}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500">Pipeline Νέα Leads</p>
                <p className="text-2xl font-bold text-purple-700 mt-1">{pipelineCounts.new}</p>
              </CardContent>
            </Card>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {leadStats.map((stat: any, index: number) => (
              <Card key={index} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">{stat.name}</p>
                      <p className="text-3xl font-bold text-slate-900">{stat.count}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${stat.bg} flex items-center justify-center ${stat.text}`}>
                      {stat.icon}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Ποσοστό Μετατροπής</CardTitle>
              <CardDescription>Επιτυχία κλεισίματος συμφωνιών επί των ανατεθειμένων lead</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <Progress value={getConversionRate()} className="h-3" />
                </div>
                <div className="text-lg font-bold text-slate-700 w-16 text-right">{getConversionRate()}%</div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                  Top Προτεραιότητα Leads
                </CardTitle>
                <CardDescription>Τα leads με το υψηλότερο score για άμεσο follow-up</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {topPriorityLeads.length > 0 ? (
                  topPriorityLeads.map((lead) => (
                    <div key={lead.id} className="rounded-lg border border-slate-200 p-3 bg-white flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{lead.name}</p>
                        <p className="text-xs text-slate-500">{lead.phone} • {getServiceLabel(lead.service)}</p>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          Score {calculateLeadScore(lead)}
                        </Badge>
                        <Badge variant="outline" className={`ml-2 ${getStageBadgeClass(lead.stage)}`}>
                          {getStageLabel(lead.stage)}
                        </Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">Δεν υπάρχουν leads για προτεραιοποίηση.</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Pipeline Snapshot</CardTitle>
                <CardDescription>Κατανομή lead ανά στάδιο</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { key: "Νέο", count: pipelineCounts.new, color: "bg-purple-500" },
                  { key: "Σε Εκκρεμότητα", count: pipelineCounts.pending, color: "bg-amber-500" },
                  { key: "Πληροφορίες", count: pipelineCounts.info, color: "bg-blue-500" },
                  { key: "Έκλεισε", count: pipelineCounts.closed, color: "bg-emerald-500" },
                ].map((item) => {
                  const value = leads.length ? Math.round((item.count / leads.length) * 100) : 0;
                  return (
                    <div key={item.key} className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">{item.key}</span>
                        <span className="font-medium text-slate-900">{item.count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`${item.color} h-full`} style={{ width: `${value}%` }} />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="sms" className="animate-fade-in">
          <PartnerSMSMetrics partnerId={partner.id} leads={leads} />
        </TabsContent>

        <TabsContent value="email" className="animate-fade-in">
          <PartnerEmailMetrics partnerId={partner.id} partnerEmail={partner.email} leads={leads} />
        </TabsContent>

        <TabsContent value="leads" className="animate-fade-in">
          <PartnerLeadsList partnerId={partner.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PartnerLeadsDashboard;
